// ============================================
// Firmflow CPA Platform — Frontend Constants
// Single source of truth for all magic values.
// ============================================

// ============================================
// APP BRANDING
// ============================================
export const APP_NAME = 'Firmflow';
export const APP_TAGLINE = 'Modern CPA Practice Management';
export const APP_DESCRIPTION = 'Streamline your CPA firm with Firmflow';
export const APP_WEBSITE = 'https://firmflow.site';
export const APP_SUPPORT_EMAIL = 'support@firmflow.site';

// ============================================
// API CONFIGURATION
// ============================================
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const API_TIMEOUT = 30000; // 30 seconds

// ============================================
// PAGINATION
// ============================================
export const PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ============================================
// CLIENT TYPES
// ============================================
export const CLIENT_TYPES = {
  INDIVIDUAL: 'individual',
  BUSINESS: 'business',
  TRUST: 'trust',
  ESTATE: 'estate',
  PARTNERSHIP: 'partnership',
};

export const CLIENT_TYPE_LABELS = {
  [CLIENT_TYPES.INDIVIDUAL]: 'Individual',
  [CLIENT_TYPES.BUSINESS]: 'Business',
  [CLIENT_TYPES.TRUST]: 'Trust',
  [CLIENT_TYPES.ESTATE]: 'Estate',
  [CLIENT_TYPES.PARTNERSHIP]: 'Partnership',
};

export const CLIENT_TYPE_OPTIONS = Object.entries(CLIENT_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

// ============================================
// TASK STATUS & PRIORITY
// ============================================
export const TASK_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  COMPLETED: 'completed',
};

export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

export const TASK_PRIORITY_COLORS = {
  [TASK_PRIORITY.LOW]: 'default',
  [TASK_PRIORITY.MEDIUM]: 'info',
  [TASK_PRIORITY.HIGH]: 'warning',
  [TASK_PRIORITY.URGENT]: 'error',
};

// ============================================
// INVOICE STATUS
// ============================================
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'PENDING',
  SENT: 'SENT',
  DONE: 'DONE',
  VOID: 'VOID',
  OVERDUE: 'overdue',
};

export const INVOICE_STATUS_COLORS = {
  [INVOICE_STATUS.DRAFT]: 'default',
  [INVOICE_STATUS.PENDING]: 'warning',
  [INVOICE_STATUS.SENT]: 'info',
  [INVOICE_STATUS.DONE]: 'success',
  [INVOICE_STATUS.VOID]: 'default',
  [INVOICE_STATUS.OVERDUE]: 'error',
};

// ============================================
// WORKFLOW STATUS
// ============================================
export const WORKFLOW_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  COMPLETE: 'complete',
  OVERDUE: 'overdue',
};

// ============================================
// USER ROLES
// ============================================
export const USER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  STAFF: 'staff',
};

export const USER_ROLE_LABELS = {
  [USER_ROLES.OWNER]: 'Owner',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.STAFF]: 'Staff',
};

// ============================================
// HEALTH SCORE
// ============================================
export const HEALTH_SCORE = {
  GOOD_THRESHOLD: 70,
  FAIR_THRESHOLD: 40,
  GOOD_LABEL: 'Good',
  FAIR_LABEL: 'Fair',
  POOR_LABEL: 'Poor',
  GOOD_COLOR: 'success',
  FAIR_COLOR: 'warning',
  POOR_COLOR: 'error',
};

// ============================================
// FILE UPLOAD
// ============================================
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 25,
  MAX_SIZE_BYTES: 25 * 1024 * 1024,
  ALLOWED_TYPES: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'csv'],
  ALLOWED_MIME_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg',
    'image/gif',
    'text/plain',
    'text/csv',
  ],
};

// ============================================
// FORM LIMITS
// ============================================
export const FORM_LIMITS = {
  NAME_MAX: 100,
  NAME_MIN: 2,
  EMAIL_MAX: 254,
  PHONE_MAX: 20,
  DESCRIPTION_MAX: 2000,
  NOTES_MAX: 5000,
  TITLE_MAX: 200,
  TITLE_MIN: 3,
  COMPANY_NAME_MAX: 200,
  ADDRESS_MAX: 500,
  TAG_MAX: 50,
  INVOICE_AMOUNT_MAX: 10000000,
  HOURS_MAX: 24,
  HOURS_MIN: 0.25,
  PASSWORD_MIN: 8,
};

// ============================================
// TIME & DATES
// ============================================
export const TIME = {
  INACTIVITY_TIMEOUT_MS: 30 * 60 * 1000, // 30 minutes
  NOTIFICATION_POLL_MS: 30 * 1000, // 30 seconds
  DUPLICATE_CHECK_DEBOUNCE_MS: 500,
  SNACKBAR_DURATION_MS: 4000,
  TOKEN_REFRESH_BUFFER_MS: 5 * 60 * 1000, // 5 min before expiry
};

// ============================================
// MESSAGES - SUCCESS
// ============================================
export const SUCCESS_MESSAGES = {
  CLIENT_CREATED: 'Client created successfully',
  CLIENT_UPDATED: 'Client updated successfully',
  CLIENT_DELETED: 'Client deleted successfully',
  TASK_CREATED: 'Task created successfully',
  TASK_COMPLETED: 'Task marked as completed',
  INVOICE_CREATED: 'Invoice created successfully',
  INVOICE_SENT: 'Invoice sent to client',
  INVOICE_PAID: 'Payment recorded successfully',
  WORKFLOW_ASSIGNED: 'Workflow assigned successfully',
  MESSAGE_SENT: 'Message sent',
  NOTE_SAVED: 'Note saved',
  FILE_UPLOADED: 'File uploaded successfully',
  SETTINGS_SAVED: 'Settings saved',
  INVITE_SENT: 'Portal invitation sent',
  SIGNATURE_SENT: 'Document sent for signature',
  SIGNATURE_COMPLETE: 'Document signed successfully',
};

// ============================================
// MESSAGES - ERROR
// ============================================
export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'Session expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  DUPLICATE_EMAIL: 'A client with this email already exists.',
  DUPLICATE_PHONE: 'A client with this phone number already exists.',
  DUPLICATE_SSN: 'A client with this SSN already exists.',
  FILE_TOO_LARGE: 'File size cannot exceed 25MB.',
  FILE_TYPE_INVALID: 'File type not allowed.',
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  PASSWORD_TOO_WEAK: 'Password must be at least 8 characters with uppercase and number.',
};

// ============================================
// ROUTES
// ============================================
export const ROUTES = {
  DASHBOARD: '/dashboard',
  CLIENTS: '/client_list',
  CLIENT_DETAIL: '/client',
  TASKS: '/tasks',
  WORKFLOWS: '/workflows',
  REPORTS: '/reports',
  SETTINGS: '/settings',
  BILLING: '/billing',
  NOTIFICATIONS: '/notifications',
  PLATFORM_ADMIN: '/platform-admin',
  EVENTS: '/events',
  TIME_ENTRIES: '/time-entries',
  INBOX: '/inbox',
  FILES: '/files',
  PROFILE: '/profile',
};

// ============================================
// LOCAL STORAGE KEYS
// ============================================
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  USER_PROFILE: 'user_profile',
  COMPANY: 'company',
  LAST_ACTIVE: 'last_active',
  THEME_MODE: 'theme_mode',
  JWT_TOKEN: 'jwtToken',
};

// ============================================
// NOTIFICATION TYPES
// ============================================
export const NOTIFICATION_TYPES = {
  TASK_ASSIGNED: 'task_assigned',
  TASK_OVERDUE: 'task_overdue',
  NEW_MESSAGE: 'new_message',
  INVOICE_PAID: 'invoice_paid',
  DOCUMENT_UPLOADED: 'document_uploaded',
  ESIGN_COMPLETE: 'esign_complete',
  ORGANIZER_SUBMITTED: 'organizer_submitted',
  INVOICE_OVERDUE: 'invoice_overdue',
  MENTION: 'mention',
  CLIENT_REPLY: 'client_reply',
};

// ============================================
// THEME
// ============================================
export const THEME = {
  SIDEBAR_WIDTH: 64,
  HEADER_HEIGHT: 64,
  BORDER_RADIUS: 8,
  CARD_ELEVATION: 1,
};

// ============================================
// LIFECYCLE STAGES
// ============================================
export const LIFECYCLE_STAGES = {
  PROSPECT: 'prospect',
  ONBOARDING: 'onboarding',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived',
};

// ============================================
// PORTAL ACCESS
// ============================================
export const PORTAL_ACCESS = {
  FULL: 'full',
  VIEW_ONLY: 'view_only',
  NO_ACCESS: 'no_access',
};

export const PORTAL_ACCESS_LABELS = {
  [PORTAL_ACCESS.FULL]: 'Full Access',
  [PORTAL_ACCESS.VIEW_ONLY]: 'View Only',
  [PORTAL_ACCESS.NO_ACCESS]: 'No Portal Access',
};

// ============================================
// ESIGNATURE STATUS
// ============================================
export const ESIGN_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  SIGNED: 'signed',
  DECLINED: 'declined',
};
